import React, { useState, useRef } from 'react';
import { apiService, downloadBlob } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Users
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ImportPage() {
  const { selectedSchoolId, isSuperAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error('Please drop a CSV file');
        return;
      }
      setFile(droppedFile);
      setPreview(null);
      setResult(null);
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    // Super admin should select a school for proper duplicate detection
    if (isSuperAdmin && !selectedSchoolId) {
      toast.warning('Please select a school from the sidebar for accurate duplicate detection');
    }

    setLoading(true);
    try {
      const response = await apiService.previewStudentImport(file, selectedSchoolId);
      setPreview(response.data);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview file');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (skipDuplicates = true) => {
    if (!file) return;
    
    // Super admin must select a school
    if (isSuperAdmin && !selectedSchoolId) {
      toast.error('Please select a school from the sidebar before importing');
      return;
    }

    setImporting(true);
    try {
      const response = await apiService.confirmStudentImport(file, skipDuplicates, selectedSchoolId);
      setResult(response.data);
      if (response.data.created_count > 0) {
        toast.success(`Successfully imported ${response.data.created_count} students`);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import students');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiService.getImportTemplate();
      downloadBlob(response.data, 'student_import_template.csv');
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="page-container" data-testid="import-page">
      <div className="page-header">
        <h1 className="page-title">Import Students</h1>
        <p className="page-subtitle">Bulk upload student roster from CSV file</p>
      </div>

      {/* Super Admin School Selection Warning */}
      {isSuperAdmin && !selectedSchoolId && (
        <div className="card mb-6 border-amber-200 bg-amber-50">
          <div className="card-content">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Select a School First</p>
                <p className="text-sm text-amber-700">As a Super Admin, please select a school from the sidebar dropdown before importing students.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="card mb-6">
        <div className="card-content">
          <h3 className="text-sm font-medium text-[#1C1C1C] mb-3">CSV Format Requirements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[#595959] mb-2"><strong>Required columns:</strong></p>
              <ul className="list-disc list-inside text-[#595959] space-y-1">
                <li>first_name</li>
                <li>last_name</li>
                <li>grade (K, 1-12)</li>
                <li>teacher_email</li>
              </ul>
              <p className="text-xs text-[#2C4C3B] mt-2 font-medium">
                Teacher email is required so student assignments, progress updates, and future messaging stay connected correctly.
              </p>
            </div>
            <div>
              <p className="text-[#595959] mb-2"><strong>Optional columns:</strong></p>
              <ul className="list-disc list-inside text-[#595959] space-y-1">
                <li>student_id_number (school's student ID, max 50 chars)</li>
                <li>teacher_name (will be suggested from email if not provided)</li>
                <li>school_name</li>
                <li>concern_area (use ; for multiple)</li>
                <li>current_tier (Tier 1, Tier 2, Tier 3)</li>
                <li>baseline_score, baseline_measure_name, goal_score</li>
                <li>support_start_date, review_date (YYYY-MM-DD)</li>
                <li>status (New Student, Improving, Limited Progress, Needs Review, Active, Monitoring, Exited)</li>
                <li>notes</li>
              </ul>
            </div>
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </div>
      </div>

      {/* File Upload */}
      {!result && (
        <div className="card mb-6">
          <div className="card-content">
            <div 
              className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors ${
                isDragging 
                  ? 'border-[#2C4C3B] bg-[#2C4C3B]/5' 
                  : 'border-[#E5E5E5] hover:border-[#2C4C3B]/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
                data-testid="csv-upload-input"
              />
              <label
                htmlFor="csv-upload"
                className="flex flex-col items-center cursor-pointer"
              >
                <Upload className={`h-12 w-12 mb-4 ${isDragging ? 'text-[#2C4C3B]' : 'text-[#A3A3A3]'}`} />
                <span className="text-sm font-medium text-[#1C1C1C]">
                  {file ? file.name : 'Click to select or drag and drop CSV file'}
                </span>
                <span className="text-xs text-[#595959] mt-1">
                  {file ? `${(file.size / 1024).toFixed(1)} KB` : 'CSV files only'}
                </span>
              </label>
            </div>

            {file && !preview && (
              <div className="mt-4 flex justify-center gap-3">
                <Button 
                  onClick={handlePreview} 
                  disabled={loading}
                  className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
                  data-testid="preview-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      <span>Preview Import</span>
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleReset}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Results */}
      {preview && !result && (
        <div className="card mb-6">
          <div className="card-header">
            <span>Import Preview</span>
          </div>
          <div className="card-content">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-[#F3F4F1] rounded-lg">
                <div className="text-2xl font-bold text-[#1C1C1C]">{preview.total_rows}</div>
                <div className="text-sm text-[#595959]">Total Rows</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{preview.valid_count}</div>
                <div className="text-sm text-green-600">Valid</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{preview.duplicate_count}</div>
                <div className="text-sm text-yellow-600">Duplicates</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{preview.error_count}</div>
                <div className="text-sm text-red-600">Errors</div>
              </div>
            </div>

            {/* Error Rows */}
            {preview.error_rows.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Rows with Errors ({preview.error_rows.length})
                </h4>
                <div className="bg-red-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {preview.error_rows.map((row, idx) => (
                    <div key={idx} className="text-sm py-1 border-b border-red-100 last:border-0">
                      <span className="font-medium text-red-800">Row {row.row_number}:</span>
                      <span className="text-red-600 ml-2">{row.error}</span>
                      <span className="text-red-400 ml-2">({row.first_name} {row.last_name})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Duplicate Warning */}
            {preview.duplicate_count > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Potential Duplicates Found</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {preview.duplicate_count} students already exist with matching name and grade.
                      These will be skipped during import to prevent duplicates.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Valid Rows Preview */}
            {preview.valid_rows.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Ready to Import ({preview.valid_rows.filter(r => !r.is_duplicate).length} new students)
                </h4>
                <div className="overflow-x-auto max-h-64">
                  <table className="data-table text-sm">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Grade</th>
                        <th>Teacher Email</th>
                        <th>Tier</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.valid_rows.slice(0, 10).map((row, idx) => (
                        <tr key={idx} className={row.is_duplicate ? 'opacity-50' : ''}>
                          <td>{row.row_number}</td>
                          <td>
                            {row.first_name} {row.last_name}
                            {row.is_duplicate && (
                              <span className="ml-2 text-xs text-yellow-600">(duplicate)</span>
                            )}
                          </td>
                          <td>{row.grade}</td>
                          <td>
                            <div>
                              <span className="text-[#1C1C1C]">{row.teacher_name || row.teacher_email.split('@')[0]}</span>
                              <span className="text-xs text-[#A3A3A3] ml-1">({row.teacher_email})</span>
                            </div>
                          </td>
                          <td>{row.current_tier}</td>
                          <td>
                            {row.is_duplicate ? (
                              <span className="text-yellow-600">Skip</span>
                            ) : (
                              <span className="text-green-600">Ready</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.valid_rows.length > 10 && (
                    <p className="text-sm text-[#595959] mt-2 text-center">
                      ...and {preview.valid_rows.length - 10} more rows
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
              <Button variant="outline" onClick={handleReset}>
                Cancel
              </Button>
              <Button
                onClick={() => handleImport(true)}
                disabled={importing || preview.valid_count === 0 || preview.valid_count === preview.duplicate_count}
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
                data-testid="confirm-import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    <span>Import {preview.valid_count - preview.duplicate_count} Students</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Result */}
      {result && (
        <div className="card mb-6">
          <div className="card-content">
            <div className="text-center py-8">
              {result.created_count > 0 ? (
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              ) : (
                <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              )}
              <h3 className="text-xl font-semibold text-[#1C1C1C] mb-2">
                Import Complete
              </h3>
              <div className="flex justify-center gap-8 mb-6">
                <div>
                  <span className="text-3xl font-bold text-green-600">{result.created_count}</span>
                  <p className="text-sm text-[#595959]">Created</p>
                </div>
                <div>
                  <span className="text-3xl font-bold text-yellow-600">{result.skipped_count}</span>
                  <p className="text-sm text-[#595959]">Skipped</p>
                </div>
                <div>
                  <span className="text-3xl font-bold text-red-600">{result.error_count}</span>
                  <p className="text-sm text-[#595959]">Errors</p>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="text-left mb-6 max-w-md mx-auto">
                  <h4 className="text-sm font-medium text-red-700 mb-2">Errors:</h4>
                  <ul className="text-sm text-red-600 space-y-1">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={handleReset}>
                  Import More
                </Button>
                <Link to="/students">
                  <Button>
                    <Users className="h-4 w-4 mr-2" />
                    View Students
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
